import type { ClientLifecyclePreview } from "@closer/shared";
import { Button, Checkbox, Modal, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * ARŞİVLEME / YENİDEN AKTİFLEŞTİRME ONAYI (CLOS-172 `[119]`).
 *
 * ### ⚠ Etki ÖNCE gösteriliyor
 *
 * Kart: *"aktif sözleşme, temsilci, hizmet ilişkisi ve açık workflow etkileri işlem öncesi
 * güvenli özetle gösterilir; çelişkili durum SESSİZCE KAYBOLMAZ."* Düğmeye basınca olan bir
 * arşivleme, kullanıcıya neyin bağlı olduğunu hiç göstermezdi.
 *
 * ### ⚠ Onayın gerekli olup olmadığını SUNUCU söylüyor
 *
 * Politika sürümlü (`config/clientLifecycle.json`) ve önizleme yanıtında geliyor. Arayüz
 * "etki varsa onay iste" kuralını kendi yazsaydı, politika değiştiğinde iki taraf ayrışır ve
 * kullanıcı sunucudan 422 alırdı.
 *
 * ### ⚠ Engel varsa düğme YOK
 *
 * Basınca 403 alacak bir düğme, kullanıcıya sebebi okumadan denemeyi öğretirdi.
 */

export function ClientLifecycleDialog({
  clientId,
  action,
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  clientId: string;
  action: "archive" | "reactivate";
  open: boolean;
  onClose: () => void;
  onConfirm: (girdi: { version: number; acknowledge: boolean }) => void;
  isPending: boolean;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const [onaylandi, setOnaylandi] = useState(false);

  const sorgu = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "client-lifecycle", clientId, action),
    queryFn: async () =>
      (
        await api<ClientLifecyclePreview>(`/api/clients/${clientId}/lifecycle/${action}/preview`, {
          tenantId,
        })
      ).data,
  });

  const onizleme = sorgu.data;
  const engelli = (onizleme?.blockedBy.length ?? 0) > 0;
  const gonderilebilir = Boolean(onizleme) && (!onizleme!.acknowledgeRequired || onaylandi);

  function kapat() {
    setOnaylandi(false);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={kapat}
      title={action === "archive" ? "Müvekkili arşivle" : "Müvekkili yeniden aktifleştir"}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={kapat}>
            Vazgeç
          </Button>
          {onizleme && !engelli && (
            <Button
              type="button"
              loading={isPending}
              disabled={!gonderilebilir}
              onClick={() => onConfirm({ version: onizleme.version, acknowledge: onaylandi })}
            >
              {action === "archive" ? "Arşivle" : "Yeniden aktifleştir"}
            </Button>
          )}
        </div>
      }
    >
      {sorgu.isPending ? (
        <div role="status" aria-label="Etkiler yükleniyor">
          <Skeleton lines={4} />
        </div>
      ) : sorgu.error || !onizleme ? (
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-3 text-sm">
          {engelli && (
            <p className="text-fg">
              Bu işlem şu ilişkiler nedeniyle yapılamıyor: {onizleme.blockedBy.join(", ")}.
            </p>
          )}

          {onizleme.impacts.filter((e) => e.count > 0).length > 0 && (
            <ul className="flex flex-col gap-1 text-fg-soft">
              {onizleme.impacts
                .filter((e) => e.count > 0)
                .map((e) => (
                  <li key={e.key}>
                    {e.label}: {e.count} kayıt
                  </li>
                ))}
            </ul>
          )}

          {onizleme.warnings.map((uyari) => (
            <p key={uyari} className="text-fg-soft">
              {uyari}
            </p>
          ))}

          {onizleme.acknowledgeRequired && !engelli && (
            <Checkbox
              label="Etkileri okudum ve onaylıyorum"
              checked={onaylandi}
              onChange={(e) => setOnaylandi(e.target.checked)}
            />
          )}
        </div>
      )}
    </Modal>
  );
}
